import { useEffect, useState, useMemo, useRef } from "react";
import { searchClient } from "@/utils/searchClients";
import Cookies from "js-cookie";
import { checkSearchLang } from "@/utils/global";
import { transformAlgoliaResults } from "@/utils/algolia";

const HITS_PER_PAGE = 32;

function useSearchData(
  query,
  page,
  selectedFilterKeys,
  currentRefinement,
  locale,
  device,
  initialData = null
) {
  const [products, setProducts] = useState(initialData?.products || []);
  const [suggestions, setSuggestions] = useState(
    initialData?.suggestions || []
  );
  const [numberOfPages, setNumberOfPages] = useState(
    initialData?.numberOfPages || 0
  );
  const [numberOfHits, setNumberOfHits] = useState(
    initialData?.numberOfHits || 0
  );
  const [queryId, setQueryId] = useState(initialData?.queryId || "");
  const [banners, setBanners] = useState(initialData?.banners || []);
  const [regionId, setRegionId] = useState(+Cookies.get("region_id") || 1101);

  const hasFetchedOnce = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const newRegionId = +Cookies.get("region_id") || 1101;
      if (newRegionId !== regionId) {
        setRegionId(newRegionId);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [regionId]);

  const indexName = useMemo(() => {
    return checkSearchLang(query?.q, locale) === "ar"
      ? currentRefinement
      : currentRefinement?.replace("ar", "en");
  }, [query?.q, locale, currentRefinement]);

  useEffect(() => {
    if (!query?.q) return;

    // Skip fetching on first render if initialData exists
    if (initialData && !hasFetchedOnce.current) {
      hasFetchedOnce.current = true;
      return;
    }

    const fetchData = async () => {
      try {
        const index = searchClient.initIndex(indexName);
        const results = await index.search(query.q, {
          page: page - 1,
          hitsPerPage: HITS_PER_PAGE,
          facetFilters: [selectedFilterKeys],
          filters: `stock_region_ids.${regionId} > 0`,
          clickAnalytics: true,
          analytics: true,
          analyticsTags: [device === "mobile" ? "Web Mobile" : "Web"],
        });

        const transformed = transformAlgoliaResults(results);
        setProducts(transformed.products);
        setSuggestions(transformed.suggestions);
        setNumberOfPages(transformed.numberOfPages);
        setNumberOfHits(transformed.numberOfHits);
        setQueryId(transformed.queryId);
        setBanners(transformed.banners);
      } catch (error) {
        console.error("Algolia fetch error", error);
      }
    };

    fetchData();
    hasFetchedOnce.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.q, page, selectedFilterKeys, indexName, regionId]);

  return {
    products,
    suggestions,
    numberOfPages,
    numberOfHits,
    queryId,
    banners,
  };
}

export default useSearchData;
